import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { t } from 'i18next';
import moment from 'moment-timezone';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// DO NOT REMOVE!!!
// This is important otherwise leaflet will bundle with each chunk that uses it
import 'patches/leaflet';
import 'leaflet-editable';
import 'leaflet-routing-machine-light/dist/leaflet-routing-machine.css';
import 'leaflet-routing-machine-light/src';
import 'leaflet.path.drag';

import { SettingsCategories } from 'models/settings';
import { WebSocketsAuthEvents, WebSocketsModules } from 'models/web-sockets';
import * as authActions from 'store/actions/auth';
import * as connectionsActions from 'store/actions/connections';
import * as uiActions from 'store/actions/ui';
import useOperationType from 'utils/hooks/use-operation-type';
import usePerm from 'utils/hooks/use-perm';
import { Route, useRoutes } from 'utils/hooks/use-routes';
import { serverAxios } from 'utils/http';

import AppView from 'App.view';
import useInitTheme from 'utils/hooks/use-init-theme';
import { SocketIO } from 'utils/web-sockets';
import appData from '../package.json';
import { AppRouteType, IAppRoute } from './App.model';
import { AppState } from './models/app-store';
import { GuardData } from './routes/guards/guard.model';
import { SessionStorage } from 'utils/enums/storage';
import sessionState from 'utils/session-storage';

const trimSlashes = (path: string): string => path.replace(/^\/+|\/+$/g, '');

const App: React.FC = () => {
  useInitTheme(useRef(false));
  const [showToastState, setShowToastState] = useState<boolean>(false);

  const dispatch = useDispatch();
  const routes = useRoutes();
  const perm = usePerm();
  const { operationType } = useOperationType();
  const store = useSelector((state: AppState) => state);
  const isAuthenticated = useSelector((state: AppState) => !!state.auth.session?.accessToken);
  const loadingSpinner = useSelector((state: AppState) => state.ui.loadingSpinner);
  const accessToken = useSelector((state: AppState) => state.auth.session?.accessToken);
  const toast = useSelector((state: AppState) => state.ui.toast);
  const timezone = useSelector((state: AppState) => state.settings.settings?.[SettingsCategories.Geolocation].timezone) || '';
  
  moment.tz.setDefault(timezone);
  // Update the locale to start the week on Monday
  moment.updateLocale('en', {
    week: {
      dow: 1, // Monday is the first day of the week
    },
  });

  useEffect(() => {
    console.log(`%c 🌈 UI Version: v${appData.version} `, 'color: pink; font-weight: bold; background-color: black;');
  }, []);

  useEffect(() => {
    /**
     * When Chrome's "Preload pages" is enabled, it may pre-render the page in the background (e.g., after pasting a logedIn URL).
     * In this state, API calls or sessionStorage access may not work as expected, causing auto-login to fail.
     *
     * To prevent this, we check if the page is visible (`document.visibilityState`). If it is, we run auto-login immediately;
     * otherwise, we wait for the page to become visible before triggering the login.
     */

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isAuthenticated) {
        autoLogin();
      }
    };

    const autoLogin = async () => {
      const site = await sessionState.load<string>(SessionStorage.SiteId);
      const company = await sessionState.load<string>(SessionStorage.CompanyId);

      if (site && company) {
        dispatch(authActions.autoLogin());
      } else {
        const id = setTimeout(() => {
          dispatch(authActions.autoLogin());
        }, 100);

        return () => clearTimeout(id);
      }
    };

    if (document.visibilityState === 'visible' && !isAuthenticated) {
      autoLogin();
    } else {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch, isAuthenticated]);

  // Add token to all the requests
  useEffect(() => {
    if (accessToken) {
      const tokenInterceptor = serverAxios.interceptors.request.use((request: AxiosRequestConfig) => {
        request.headers = { ...request.headers, Authorization: `Bearer ${accessToken}` };

        return request;
      });

      return () => {
        serverAxios.interceptors.request.eject(tokenInterceptor);
      };
    }
  }, [accessToken]);

  // Add logout when token invalid sent back from the server
  useEffect(() => {
    if (isAuthenticated) {
      const logoutInterceptor = serverAxios.interceptors.response.use(
        (response: AxiosResponse): AxiosResponse => response,
        (error) => {
          if (error?.response?.data.error && ['bad token', 'invalid token'].includes(error.response.data.error)) {
            console.error('Error ' + error.response.data.error.toString());

            dispatch(authActions.logout());
          }

          throw error;
        },
      );

      return () => {
        serverAxios.interceptors.response.eject(logoutInterceptor);
      };
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    // Web socket connection are for logged in users only (token required)
    if (!isAuthenticated || !accessToken) {
      // User is not authenticated - reset any registered listeners
      SocketIO.clearListeners();

      return;
    }

    const socket = new SocketIO(accessToken);

    socket.onConnect((socket) => {
      dispatch(connectionsActions.setSocket(socket));
    });

    socket.on(WebSocketsModules.Auth, WebSocketsAuthEvents.TwoFactorAuthEnforced, () => {
      console.log('Site is now enforcing 2FA, renewing current session.');
      dispatch(uiActions.hideToast());

      /** Dispatching with slight delay to ensure toast is cleared first */
      setTimeout(() => {
        dispatch(uiActions.showSuccessToast({ message: t('auth.toast.securityUpdate') }));
        /** Try to renew session (instead of logging-out) to improve UX on trusted devices */
        dispatch(authActions.autoLogin());
      }, 100);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    socket.onDisconnect((reason: string, details: any) => {
      console.log('Socket Disconnected!, disconnection reason:', reason, details ?? '');
    });

    return () => {
      socket.getSocket().disconnect();
    };
  }, [dispatch, isAuthenticated, accessToken]);

  const prepareRouteList = (routes: Route[]) => {
    const guardData: GuardData = { store, perm, operationType };

    return routes.reduce((acc: IAppRoute[], route: Route) => {
      if (route.guard && !route.guard.every((guard) => guard(guardData))) {
        /**
         * A fallback that points at the path it replaces redirects to itself forever and freezes the app,
         * so such a route is left out and the catch-all route takes the user to a page they can open.
         */
        const isSelfRedirect = !!route.fallback && trimSlashes(route.fallback) === trimSlashes(route.path);

        if (route.fallback && !isSelfRedirect) {
          const contentRoute: IAppRoute = {
            type: AppRouteType.Fallback,
            path: route.path,
            to: route.fallback,
            shouldWaitForAppParameters: route.shouldWaitForAppParameters,
            children: route.children ? prepareRouteList(route.children) : null,
          };

          return [...acc, contentRoute];
        }

        return acc;
      }

      const component: React.FC | undefined = route['component'];
      const jsx: JSX.Element = route['jsx'];

      const contentRoute: IAppRoute = {
        type: AppRouteType.Page,
        path: route.path,
        pageName: route.name,
        component,
        jsx,
        shouldWaitForAppParameters: route.shouldWaitForAppParameters,
        children: route.children ? prepareRouteList(route.children) : null,
      };

      return [...acc, contentRoute];
    }, []);
  };

  useEffect(() => {
    setShowToastState(() => !!toast);
  }, [toast, setShowToastState]);

  return (
    <AppView
      loadingSpinner={loadingSpinner}
      isAuthenticated={isAuthenticated}
      toast={toast}
      showToast={showToastState}
      routesList={prepareRouteList(routes)}
      onHideToast={() => dispatch(uiActions.hideToast())}
      onToastShown={() => setShowToastState(() => false)}
    ></AppView>
  );
};

App.displayName = 'App';

export default App;
